/**
 * GraphQL Authentication Mutation Hooks
 *
 * Hooks for auth operations: register, login, verify email, password reset.
 */

import { useMutation, gql } from '@apollo/client';
import { apolloClient } from '../../graphql/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        emailVerified
        createdAt
        updatedAt
      }
      token
    }
  }
`;

const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        emailVerified
        createdAt
        updatedAt
      }
      token
    }
  }
`;

const VERIFY_EMAIL_MUTATION = gql`
  mutation VerifyEmail($input: VerifyEmailInput!) {
    verifyEmail(input: $input) {
      success
      message
    }
  }
`;

const RESEND_VERIFICATION_MUTATION = gql`
  mutation ResendVerification($input: ResendVerificationInput!) {
    resendVerification(input: $input) {
      success
      message
    }
  }
`;

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($input: ForgotPasswordInput!) {
    forgotPassword(input: $input) {
      success
      message
    }
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      success
      message
    }
  }
`;

// Types
interface User {
  id: string;
  globalId: string;
  email: string;
  username: string;
  slug: string;
  displayName: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthPayload {
  user: User;
  token: string;
}

interface SuccessPayload {
  success: boolean;
  message?: string;
}

// Input types
interface RegisterInput {
  email: string;
  username: string;
  displayName: string;
  password: string;
}

interface LoginInput {
  email: string;
  password: string;
}

interface VerifyEmailInput {
  token: string;
}

interface ResendVerificationInput {
  email: string;
}

interface ForgotPasswordInput {
  email: string;
}

interface ResetPasswordInput {
  token: string;
  password: string;
}

/**
 * Hook for user registration
 */
export function useRegisterGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { register: AuthPayload },
    { input: RegisterInput }
  >(REGISTER_MUTATION);

  const register = async (input: RegisterInput) => {
    logger.info('Auth', 'Registering user via GraphQL', { username: input.username });

    const result = await mutate({ variables: { input } });

    if (result.data?.register) {
      // Store token and user in localStorage (matching REST behavior)
      localStorage.setItem('token', result.data.register.token);
      localStorage.setItem('user', JSON.stringify(result.data.register.user));

      // Reset Apollo cache on login
      await apolloClient.resetStore();

      logger.info('Auth', 'Registration successful', { userId: result.data.register.user.id });
    }

    return result.data?.register;
  };

  return {
    register,
    data: data?.register,
    loading,
    error,
  };
}

/**
 * Hook for user login
 */
export function useLoginGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { login: AuthPayload },
    { input: LoginInput }
  >(LOGIN_MUTATION);

  const login = async (input: LoginInput) => {
    logger.info('Auth', 'Logging in via GraphQL', { email: input.email });

    const result = await mutate({ variables: { input } });

    if (result.data?.login) {
      // Store token and user in localStorage (matching REST behavior)
      localStorage.setItem('token', result.data.login.token);
      localStorage.setItem('user', JSON.stringify(result.data.login.user));

      // Reset Apollo cache on login
      await apolloClient.resetStore();

      logger.info('Auth', 'Login successful', { userId: result.data.login.user.id });
    }

    return result.data?.login;
  };

  return {
    login,
    data: data?.login,
    loading,
    error,
  };
}

/**
 * Hook for email verification
 */
export function useVerifyEmailGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { verifyEmail: SuccessPayload },
    { input: VerifyEmailInput }
  >(VERIFY_EMAIL_MUTATION);

  const verifyEmail = async (token: string) => {
    logger.info('Auth', 'Verifying email via GraphQL');

    const result = await mutate({ variables: { input: { token } } });

    return result.data?.verifyEmail;
  };

  return {
    verifyEmail,
    data: data?.verifyEmail,
    loading,
    error,
  };
}

/**
 * Hook for resending verification email
 */
export function useResendVerificationGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { resendVerification: SuccessPayload },
    { input: ResendVerificationInput }
  >(RESEND_VERIFICATION_MUTATION);

  const resendVerification = async (email: string) => {
    logger.info('Auth', 'Resending verification via GraphQL', { email });

    const result = await mutate({ variables: { input: { email } } });

    return result.data?.resendVerification;
  };

  return {
    resendVerification,
    data: data?.resendVerification,
    loading,
    error,
  };
}

/**
 * Hook for forgot password
 */
export function useForgotPasswordGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { forgotPassword: SuccessPayload },
    { input: ForgotPasswordInput }
  >(FORGOT_PASSWORD_MUTATION);

  const forgotPassword = async (email: string) => {
    logger.info('Auth', 'Requesting password reset via GraphQL', { email });

    const result = await mutate({ variables: { input: { email } } });

    return result.data?.forgotPassword;
  };

  return {
    forgotPassword,
    data: data?.forgotPassword,
    loading,
    error,
  };
}

/**
 * Hook for password reset
 */
export function useResetPasswordGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { resetPassword: SuccessPayload },
    { input: ResetPasswordInput }
  >(RESET_PASSWORD_MUTATION);

  const resetPassword = async (token: string, password: string) => {
    logger.info('Auth', 'Resetting password via GraphQL');

    const result = await mutate({ variables: { input: { token, password } } });

    return result.data?.resetPassword;
  };

  return {
    resetPassword,
    data: data?.resetPassword,
    loading,
    error,
  };
}

/**
 * Combined hook for all auth mutations
 */
export function useAuthMutationsGraphQL() {
  return {
    useRegister: useRegisterGraphQL,
    useLogin: useLoginGraphQL,
    useVerifyEmail: useVerifyEmailGraphQL,
    useResendVerification: useResendVerificationGraphQL,
    useForgotPassword: useForgotPasswordGraphQL,
    useResetPassword: useResetPasswordGraphQL,
  };
}
